/**
 * Canonical section-display contract shared between `ModuleCard.vue`, table views
 * (`SelfStudyListTableView.vue`, `ClassGroupResourceTableView.vue`) and the pure
 * util functions in `@modules/pending-action/service/section-display.utils.ts`.
 *
 */

/** Module/section completion status as computed by BE. */
export const MODULE_COMPLETION_STATUS = {
  INCOMPLETE: "INCOMPLETE",
  COMPLETE: "COMPLETE",
  PAUSED: "PAUSED",
} as const;

export type ModuleCompletionStatus =
  (typeof MODULE_COMPLETION_STATUS)[keyof typeof MODULE_COMPLETION_STATUS];

/**
 * Canonical, BE-driven model describing which section (if any) should be
 * displayed on a module/resource card or table row.
 *
 * BE contract mapping (SLS-12369 §0.4):
 * - `sectionInfo.hideSectionActivityNumber` -> `sectionNumberHidden` (module-level "Hide Section/Activity
 *   Number" setting, `LessonSettingsView.vue`) — only suppresses the NUMBER, falls back to section title.
 * - `sectionInfo.hideSectionCover` -> `sectionCoverHidden` (section-level "viewable" toggle,
 *   `CoursePlanSectionComponent.vue` `showSectionCover`/`viewable_label`) — when the section is NOT
 *   viewable, the entire "Section {num}" line must be hidden (no title fallback). This is distinct
 *   from `sectionNumberHidden` and must NOT be conflated with it (see `resolveSectionDisplayText`).
 * - `sectionInfo.sectionDisplayModel.pageNo` -> `selectedSectionNumber`
 * - `sectionInfo.sectionDisplayModel.title` -> `selectedSectionTitle`
 * - `sectionInfo.sectionDisplayModel.status` -> completion status (self-study only)
 * - Module without any section -> BE returns an empty `SectionDisplayModel` (id=0, title=null)
 */
export interface SectionDisplayModel {
  status: ModuleCompletionStatus;
  hasSections: boolean;
  /** Module-level "Hide Section/Activity Number" setting — suppresses number, falls back to title. */
  sectionNumberHidden: boolean;
  /**
   * Section-level "viewable" toggle is OFF (`hideSectionCover=true`). When `true`, the whole
   * section line must render as blank — takes precedence over `sectionNumberHidden`/title fallback.
   */
  sectionCoverHidden: boolean;
  /** Section already selected by BE according to status (first-incomplete / last). */
  selectedSectionNumber?: number | null;
  selectedSectionTitle?: string | null;
  /** Planner-only exclusion rule (AC#106/#107) — isolated per impact-assessment §7 risk note. */
  isFullModuleAttached?: boolean;
}

/** Tooltip copy shown on hover for `ModuleCard.vue` icons, distinct from `ariaLabelConfig`. */
export interface ModuleCardTooltipConfig {
  viewCount?: string;
  teacherReviewCount?: string;
  accessCode?: string;
  paused?: string;
}
